import { db } from '@db/database';
import { useUserStore } from '@stores/userStore';
import type { SyncQueueItem, SyncEntityType } from '@db/database';
import {
  getIsOnline,
  subscribe as subscribeConnectivity
} from './connectivityService';
import { reportSyncFailure } from './errorReportingService';

const BASE_DELAY = 1000;
const MAX_BACKOFF = 60000;
const SYNCED_CLEANUP_AGE_MS = 60 * 60 * 1000; // 1 hour
const ONLINE_DEBOUNCE_MS = 2000;
const LOCK_NAME = 'offlineSyncService_processAll';
const FAILED_CLEANUP_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const DEDUP_ENTITY_TYPES: SyncEntityType[] = [
  'calibration_save',
  'acknowledgment'
];

const DEFAULT_MAX_RETRIES_BY_TYPE: Partial<Record<SyncEntityType, number>> = {
  calibration_save: 10
};
const DEFAULT_MAX_RETRIES = 5;

export type SyncHandler = (item: SyncQueueItem) => Promise<void>;
export type SyncStatusCounts = {
  pending: number;
  failed: number;
  syncing: number;
};
export type SyncListener = () => void;
export type EnqueueParams = Omit<
  SyncQueueItem,
  | 'id'
  | '_userId'
  | 'status'
  | 'retryCount'
  | 'maxRetries'
  | 'createdAt'
  | 'updatedAt'
> & {
  maxRetries?: number;
};

export type RollbackHandler = (item: SyncQueueItem) => Promise<void>;

// --- Module-scoped private state ---
const handlers = new Map<SyncEntityType, SyncHandler>();
const rollbackHandlers = new Map<SyncEntityType, RollbackHandler>();
const listeners = new Set<SyncListener>();
let processing = false;

const syncingCalibrationIds = new Set<string>();
const CALIBRATION_SYNC_ENTITY_TYPES: SyncEntityType[] = [
  'dc_runtime_create',
  'calibration_save',
  'calibration_complete'
];

export function isSyncingCalibration(entityKey: string): boolean {
  return syncingCalibrationIds.has(entityKey);
}

// --- Private helpers ---

function getCurrentUserId(): string {
  const userName = useUserStore.getState().userName;
  if (!userName) throw new Error('No authenticated user');
  return userName;
}

function notify(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // swallow per-listener errors
    }
  }
}

function calculateNextRetryAt(retryCount: number): string {
  const delay = Math.min(
    BASE_DELAY * Math.pow(2, retryCount) + Math.random() * 1000,
    MAX_BACKOFF
  );
  return new Date(Date.now() + delay).toISOString();
}

function classifyError(error: unknown): 'permanent' | 'conflict' | 'transient' {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 409) return 'conflict';
  if (status !== undefined && status >= 400 && status < 500) return 'permanent';
  return 'transient';
}

// --- Public API ---

export async function enqueue(params: EnqueueParams): Promise<number> {
  return await db.transaction('rw', db.syncQueue, async () => {
    const userId = getCurrentUserId();
    const now = new Date().toISOString();

    // charge_delete cancellation: remove pending charge_add with same key
    if (params.entityType === 'charge_delete') {
      const existing = await db.syncQueue
        .where('[_userId+entityType+entityKey]')
        .equals([userId, 'charge_add', params.entityKey])
        .first();

      if (existing && existing.status === 'pending') {
        await db.syncQueue.delete(existing.id!);
        notify();
        return 0;
      }
    }

    // Dedup for calibration_save and acknowledgment
    if (DEDUP_ENTITY_TYPES.includes(params.entityType)) {
      const existing = await db.syncQueue
        .where('[_userId+entityType+entityKey]')
        .equals([userId, params.entityType, params.entityKey])
        .first();

      if (
        existing &&
        (existing.status === 'pending' || existing.status === 'syncing')
      ) {
        await db.syncQueue.update(existing.id!, {
          payload: params.payload,
          updatedAt: now
        });
        notify();
        return existing.id!;
      }
    }

    // Default: insert new item
    const item: SyncQueueItem = {
      _userId: userId,
      entityType: params.entityType,
      entityKey: params.entityKey,
      payload: params.payload,
      status: 'pending',
      priority: params.priority,
      dependsOn: params.dependsOn,
      retryCount: 0,
      maxRetries:
        params.maxRetries ??
        DEFAULT_MAX_RETRIES_BY_TYPE[params.entityType] ??
        DEFAULT_MAX_RETRIES,
      createdAt: now,
      updatedAt: now
    };

    const id = await db.syncQueue.add(item);
    notify();
    return id as number;
  });
}

export async function processAll(): Promise<void> {
  if (!getIsOnline() || processing) return;

  processing = true;
  try {
    const doProcess = async () => {
      const userId = getCurrentUserId();

      // Reset orphaned syncing items
      const orphaned = await db.syncQueue
        .where('status')
        .equals('syncing')
        .and(item => item._userId === userId)
        .toArray();

      for (const item of orphaned) {
        await db.syncQueue.update(item.id!, {
          status: 'pending',
          updatedAt: new Date().toISOString()
        });
      }

      // Cleanup old synced items
      const cutoff = new Date(Date.now() - SYNCED_CLEANUP_AGE_MS).toISOString();
      await db.syncQueue
        .where('status')
        .equals('synced')
        .and(item => item._userId === userId && item.updatedAt < cutoff)
        .delete();

      // Cleanup permanently failed items older than 30 days
      const failedCutoff = new Date(
        Date.now() - FAILED_CLEANUP_AGE_MS
      ).toISOString();
      await db.syncQueue
        .where('status')
        .equals('failed')
        .and(item => item._userId === userId && item.updatedAt < failedCutoff)
        .delete();

      // Fetch pending items ready to process
      const now = new Date().toISOString();
      const pending = await db.syncQueue
        .where('status')
        .equals('pending')
        .and(
          item =>
            item._userId === userId &&
            (!item.nextRetryAt || item.nextRetryAt <= now)
        )
        .sortBy('createdAt');

      // Sort by priority ASC, then createdAt ASC
      pending.sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        return a.createdAt.localeCompare(b.createdAt);
      });

      for (const item of pending) {
        // Check dependsOn
        if (item.dependsOn && item.dependsOn.length > 0) {
          let blocked = false;
          for (const depId of item.dependsOn) {
            const dep = await db.syncQueue.get(depId);
            if (dep && dep.status !== 'synced') {
              blocked = true;
              break;
            }
          }
          if (blocked) continue;
        }

        // Verify user hasn't changed
        if (getCurrentUserId() !== item._userId) break;

        const handler = handlers.get(item.entityType);
        if (!handler) {
          console.warn(
            `[OfflineSyncService] No handler registered for entity type: ${item.entityType}`
          );
          continue;
        }

        // Mark as syncing
        await db.syncQueue.update(item.id!, {
          status: 'syncing',
          updatedAt: new Date().toISOString()
        });

        const isCalibrationSync = CALIBRATION_SYNC_ENTITY_TYPES.includes(
          item.entityType
        );
        if (isCalibrationSync) {
          syncingCalibrationIds.add(item.entityKey);
        }
        notify();

        try {
          await handler(item);
          await db.syncQueue.update(item.id!, {
            status: 'synced',
            updatedAt: new Date().toISOString()
          });
        } catch (error) {
          const classification = classifyError(error);
          const errorMessage =
            error instanceof Error ? error.message : String(error);

          if (classification === 'conflict') {
            await db.syncQueue.update(item.id!, {
              status: 'synced',
              lastError: errorMessage,
              updatedAt: new Date().toISOString()
            });
          } else if (classification === 'permanent') {
            await db.syncQueue.update(item.id!, {
              status: 'failed',
              lastError: errorMessage,
              updatedAt: new Date().toISOString()
            });
            reportSyncFailure({ ...item, lastError: errorMessage });
            const rollback = rollbackHandlers.get(item.entityType);
            if (rollback) {
              try {
                await rollback(item);
              } catch {
                /* best effort */
              }
            }
          } else {
            const newRetryCount = item.retryCount + 1;
            if (newRetryCount >= item.maxRetries) {
              await db.syncQueue.update(item.id!, {
                status: 'failed',
                retryCount: newRetryCount,
                lastError: errorMessage,
                updatedAt: new Date().toISOString()
              });
              reportSyncFailure({
                ...item,
                retryCount: newRetryCount,
                lastError: errorMessage
              });
              const rollback = rollbackHandlers.get(item.entityType);
              if (rollback) {
                try {
                  await rollback(item);
                } catch {
                  /* best effort */
                }
              }
            } else {
              await db.syncQueue.update(item.id!, {
                status: 'pending',
                retryCount: newRetryCount,
                lastError: errorMessage,
                nextRetryAt: calculateNextRetryAt(newRetryCount),
                updatedAt: new Date().toISOString()
              });
            }
          }
        } finally {
          if (isCalibrationSync) {
            syncingCalibrationIds.delete(item.entityKey);
          }
        }

        notify();
      }
    };

    // Cross-tab safety via Web Lock API
    if (navigator.locks) {
      await navigator.locks.request(LOCK_NAME, doProcess);
    } else {
      await doProcess();
    }
  } finally {
    processing = false;
  }
}

export async function retryFailed(): Promise<void> {
  const userId = getCurrentUserId();

  const failed = await db.syncQueue
    .where('status')
    .equals('failed')
    .and(item => item._userId === userId)
    .toArray();

  for (const item of failed) {
    await db.syncQueue.update(item.id!, {
      status: 'pending',
      retryCount: 0,
      lastError: undefined,
      nextRetryAt: undefined,
      updatedAt: new Date().toISOString()
    });
  }

  notify();
  await processAll();
}

export async function getStatus(): Promise<SyncStatusCounts> {
  const userId = getCurrentUserId();

  const items = await db.syncQueue.where('_userId').equals(userId).toArray();

  return {
    pending: items.filter(i => i.status === 'pending').length,
    failed: items.filter(i => i.status === 'failed').length,
    syncing: items.filter(i => i.status === 'syncing').length
  };
}

export function subscribe(listener: SyncListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function registerHandler(
  entityType: SyncEntityType,
  handlerFn: SyncHandler
): void {
  handlers.set(entityType, handlerFn);
}

export function registerRollbackHandler(
  entityType: SyncEntityType,
  handlerFn: RollbackHandler
): void {
  rollbackHandlers.set(entityType, handlerFn);
}

export async function syncNow(): Promise<void> {
  const userId = getCurrentUserId();
  const now = new Date().toISOString();

  const stalled = await db.syncQueue
    .where('_userId')
    .equals(userId)
    .and(item => item.status === 'pending' || item.status === 'failed')
    .toArray();

  for (const item of stalled) {
    await db.syncQueue.update(item.id!, {
      status: 'pending',
      retryCount: 0,
      lastError: undefined,
      nextRetryAt: undefined,
      updatedAt: now
    });
  }

  notify();
  await processAll();
}

export async function dismissItem(itemId: number): Promise<void> {
  await db.syncQueue.delete(itemId);
  notify();
}

export async function clearUserItems(userId: string): Promise<void> {
  await db.syncQueue.where('_userId').equals(userId).delete();
  notify();
}

export function initializeSyncService(): void {
  subscribeConnectivity(online => {
    if (online) {
      setTimeout(async () => {
        await processAll();
        const { processPendingCerts } = await import('./pendingCertService');
        processPendingCerts();
      }, ONLINE_DEBOUNCE_MS);
    }
  });
}
